import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FuseConfigService } from '@fuse/services/config';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';
import { map, Observable } from 'rxjs';

/**
 * `GET /v1/subscriptions/me` response shape (TASK-018, `rml-platform/apps/api/src/
 * routes/subscriptions.ts` + `packages/domain/src/subscriptions/subscription-response.
 * ts`). Duplicated locally rather than imported — standalone demo front-end repo, not a
 * workspace package of `rml-platform` (same convention as `CheckoutService`'s own
 * header note) — keep in sync by hand if the API contract ever changes.
 *
 * Always a 200 with a (possibly empty) `subscriptions` array — there is no 404/empty-
 * body case for "no subscription": a member who has never subscribed gets
 * `{ subscriptions: [] }`, same as one whose only subscription was archived.
 */
export interface RmlSubscription {
    id: string;
    user_id: string;
    business_id: string | null;
    plan_id: string | null;
    scope: string;
    /** `'trialing' | 'active' | 'past_due' | 'cancelled' | 'expired' | 'continuation'`
     * (`subscriptions.status` CHECK, `0009_c2_subscriptions.sql`). Only `'active'`/
     * `'trialing'` grant content access — see `hasActiveAccess()` below. */
    status: string;
    provider: string | null;
    trial_ends_at: string | null;
    current_period_start: string | null;
    current_period_end: string | null;
    term_ends_at: string | null;
    cancel_at: string | null;
    cancelled_at: string | null;
    grace_until: string | null;
    continuation_of: string | null;
    activated_at: string | null;
    created_at: string;
    updated_at: string;
    archived_at: string | null;
}

export interface RmlSubscriptionsMeResponse {
    subscriptions: RmlSubscription[];
}

/** The only two statuses that grant content access (task acceptance criteria) — a
 * `past_due`/`cancelled`/`expired`/`continuation` subscription, or no subscription row
 * at all, means the member needs to (re)subscribe. `provider` (`'manual'`/`'payfast'`/
 * `'revenuecat'`/`'voucher'`) plays no part in this check — a `provider='manual'`
 * subscription with `status='active'` passes exactly like any other. */
const ACTIVE_ACCESS_STATUSES = ['active', 'trialing'];

/** Every legacy `subscriptionDetails(state)` call site only ever passes `'Active'`
 * (case as written at each call site) — mapped here to the real status vocabulary above
 * so `.subscription.length` keeps meaning "has an active/trialing subscription", not
 * "the legacy RPC returned any row at all". An unrecognised `state` returns every
 * subscription row unfiltered. */
const LEGACY_STATE_TO_STATUSES: Record<string, string[]> = {
    active: ACTIVE_ACCESS_STATUSES,
};

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _fuseConfigService: FuseConfigService,
        private _authService: AuthService
    ) {
    }
    options: object;
    payload: object;
    subscription: any = null;

    /** True if ANY row in `records` is `active`/`trialing` — the single source of truth
     * for "has content access" (task acceptance criteria). `null`/empty/undefined, or
     * every row `cancelled`/`expired`/`past_due`/`continuation`, means the member needs
     * to (re)subscribe. Static so callers that only hold the plain `records` array
     * (e.g. `reflections.component.ts`) don't need a `SubscriptionService` instance. */
    public static hasActiveAccess(records: RmlSubscription[] | null | undefined): boolean {
        return !!records && records.some((sub) => ACTIVE_ACCESS_STATUSES.includes(sub?.status));
    }

    // `createSubscription()`/`retryCreateSubscription()`/`updatePersonalSubscription()`/
    // `archiveTrialSubscription()` removed (fix-pass, "Manage My Plan" menu-item audit):
    // all four called retired legacy Strapi-era "brochure" or internal RPC paths, and a
    // repo-wide check confirmed zero remaining call sites for any of them (the demo's
    // "Manage My Plan" screen, their one-time consumer, is now rewired onto the real
    // `GET /v1/subscriptions/me` + `POST /v1/subscriptions/:id/cancel` routes below).
    // Removed rather than left dead, per this audit's own build-verification bar (an
    // unminified demo build keeps a literal legacy-path string reachable in `dist/`
    // even from an unused method on a `providedIn: 'root'` service).

    /**
     * `GET /v1/subscriptions/me` — rewired off the legacy, always-404ing
     * `individual/internal/user-management/grid-personal-subscription` RPC path (the
     * root cause of a newly-signed-up member never being routed into the subscribe
     * flow: that 404 resolved `subscription` to `null`, and every call site's own
     * `.subscription.length == 0` check silently evaluated `undefined == 0` →
     * `false`). Authenticated; bearer injection is automatic for any
     * `${environment.apiBase}` call (`auth.interceptor.ts`).
     *
     * The response is re-shaped to the legacy `{ data: { records: [...] } }` envelope
     * every existing call site (`sign-in.component.ts`, `social-service.ts`,
     * `reflections.component.ts`) already destructures via `response.data.records` — so
     * none of them need to change, only the plumbing behind this method. `records` is
     * filtered to the statuses `state` maps to (`LEGACY_STATE_TO_STATUSES`) so
     * `records.length` keeps meaning "has an active/trialing subscription", not just
     * "the API returned some row".
     */
    public subscriptionDetails(state: string): Observable<any> {
        const statuses = LEGACY_STATE_TO_STATUSES[state?.toLowerCase()];
        return this._httpClient.get<RmlSubscriptionsMeResponse>(`${environment.apiBase}/subscriptions/me`).pipe(
            map((response) => {
                const records = statuses
                    ? response.subscriptions.filter((sub) => statuses.includes(sub.status))
                    : response.subscriptions;
                return { data: { records } };
            }),
        );
    }

    /**
     * `POST /v1/subscriptions/:id/cancel` (TASK-018): the caller cancels their OWN
     * subscription; the ownership check happens server-side (`subscriptions.ts`'s own
     * pre-fetch). Rewired off the legacy `updatePersonalSubscription(id, 'Cancelling')`
     * call, which posted to the retired `individual/internal/user-management/update-
     * personal-subscription` path (404 against the demo Worker). There is no matching
     * "reactivate" transition on the new API (`subscriptions.ts` only ever exposes
     * `cancel`); `ManagePlanComponent` does not call one, see that component's own
     * doc comment.
     */
    public cancelSubscription(subscriptionId: string): Observable<any> {
        return this._httpClient.post(`${environment.apiBase}/subscriptions/${subscriptionId}/cancel`, {});
    }

    public gridSubscription(): Observable<any>  {
        this.payload = {
            subscription: { state: "Active" }
          };
        return this._httpClient.post(this._fuseConfigService.global.settings["api_url"] + 'visitor/brochure/subscription/grid-subscription', this.payload); 
         
    }

    public async setupSubscriptionDetails(state: string, userType: string): Promise<any> {
        return new Promise((resolve, reject) => {
            if(userType == "individual"){
                this.subscriptionDetails(state).subscribe({
                    next: async (response) => {
                        this.subscription = await response.data["records"];
                        resolve(this.subscription); 
                    },
                    error: (response) => {
                        reject(response.error.message);
                    },
                });
            }else{
                resolve("business")
            }
        });
    }

}
